import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

/**
 * 技術スタック — sticky header + スクロール連動グループ表示
 *
 * - IntersectionObserver でグループが画面に入るとフェードインアニメーション
 * - フィルターボタンクリックで対象カテゴリへスムーズスクロール
 */

const BLOCK = "stacks";
const STACKS_FADE_TRIGGER_START = "top 90%";
const STACKS_SPY_TRIGGER_START = "top 70%";
const STACKS_SPY_TRIGGER_END = "bottom 40%";
const STACKS_SCROLL_TO_DURATION = 0.55;
const STACKS_SCROLL_TO_EASE = "power2.out";

/**
 * sticky ヘッダーが占有する高さを計算する
 *
 * - モバイルでは top: 0 のため、技術スタックヘッダー自身の高さだけを避ければよい
 * - デスクトップでは top: 54px が付くため、その分も加算してスクロール先を下げる
 */
function getStacksStickyOffset(header) {
  if (!header) return 0;

  const computedTop = Number.parseFloat(window.getComputedStyle(header).top) || 0;
  return header.offsetHeight + computedTop;
}

/**
 * 技術スタックセクションのスクロール連動・フィルタ・アニメーション初期化
 *
 * - ScrollTriggerでグループのフェードイン
 * - フィルターボタンでカテゴリごとにスムーズスクロール
 * - スクロール連動でボタンのアクティブ状態を自動更新
 *
 */
export function initScrollStacks() {
  // --- 1. 必要な要素を取得 ---
  const section = document.getElementById("stacks");
  if (!section) return;

  const header = section.querySelector(`.${BLOCK}__header`); // 固定ヘッダー
  const groups = Array.from(section.querySelectorAll(`.js-${BLOCK}__group`)); // 技術グループ
  const btns = Array.from(section.querySelectorAll(`.js-${BLOCK}Filter__btn`)); // フィルターボタン

  if (!groups.length) return;

  // --- 2. ScrollTrigger: グループのフェードイン ---
  /**
   * 各グループが画面に入ったらフェードインアニメーションを付与
   * - idx * 120ms の遅延で順番に表示
   * - 一度表示したら監視解除（once: true）
   */
  groups.forEach((group, idx) => {
    ScrollTrigger.create({
      trigger: group,
      start: STACKS_FADE_TRIGGER_START,
      once: true,
      onEnter: () => {
        gsap.delayedCall(idx * 0.12, () => {
          group.classList.add("is-visible");
        });
      },
    });
  });

  // --- 3. フィルターボタン: カテゴリごとにスムーズスクロール ---
  /**
   * ボタンクリックで該当カテゴリのグループまでスムーズにスクロール
   * - "all"の場合はセクション先頭へ
   * - クリック中はisClickScrolling=trueでスクロール連動監視を一時停止
   */
  let isClickScrolling = false;
  const endClickScrolling = () => {
    isClickScrolling = false;
  };

  btns.forEach((btn) => {
    btn.addEventListener("click", () => {
      btns.forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");

      const cat = btn.dataset.category;

      if (cat === "all") {
        isClickScrolling = true;
        const stickyOffset = getStacksStickyOffset(header);
        gsap.killTweensOf(window);
        gsap.to(window, {
          duration: STACKS_SCROLL_TO_DURATION,
          ease: STACKS_SCROLL_TO_EASE,
          scrollTo: {
            y: Math.max(section.offsetTop - stickyOffset, 0),
            autoKill: true,
          },
          onComplete: endClickScrolling,
          onInterrupt: endClickScrolling,
        });
        return;
      }

      // 対象グループを探してスクロール
      const target = groups.find((g) => g.dataset.group === cat);
      if (!target) return;

      isClickScrolling = true;
      const stickyOffset = getStacksStickyOffset(header);
      const rect = target.getBoundingClientRect();
      const scrollTop = window.pageYOffset;
      gsap.killTweensOf(window);
      gsap.to(window, {
        duration: STACKS_SCROLL_TO_DURATION,
        ease: STACKS_SCROLL_TO_EASE,
        scrollTo: {
          y: Math.max(rect.top + scrollTop - stickyOffset, 0),
          autoKill: true,
        },
        onComplete: endClickScrolling,
        onInterrupt: endClickScrolling,
      });
    });
  });

  // --- 4. ScrollTrigger: ボタンのアクティブ状態自動更新 ---
  /**
    * ScrollTriggerでグループが画面に入ったら対応ボタンをアクティブ化
   * - クリックによるスクロール中はisClickScrolling=trueで無効化
   */
  // カテゴリ → ボタンのマップ
  const catBtnMap = {};
  btns.forEach((btn) => { catBtnMap[btn.dataset.category] = btn; });

  groups.forEach((group) => {
    ScrollTrigger.create({
      trigger: group,
      start: STACKS_SPY_TRIGGER_START,
      end: STACKS_SPY_TRIGGER_END,
      onToggle: (self) => {
        if (!self.isActive || isClickScrolling) return;
        const cat = group.dataset.group;
        if (!cat) return;
        btns.forEach((b) => b.classList.remove("is-active"));
        if (catBtnMap[cat]) {
          catBtnMap[cat].classList.add("is-active");
        }
      },
    });
  });
}
